/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React from 'react'
import { Modal } from 'antd'
import styled from '@emotion/styled'
import SportNinjaLogo from '../SportNinjaLogo'
import Text from '../Text'
import Button from '../Button'
import {
  OnboardingWizardProvider,
  useOnboardingWizard,
} from './context/OnboardingWizardContext'
import LeagueSetup from './steps/LeagueSetup'
import SportsSelection from './steps/SportsSelection'
import FirstCompetition from './steps/FirstCompetition'
import CloseButton from './components/CloseButton'
import colors from '@sportninja/common/constants/appColors'
import {
  getAllGenericSportProperties,
  getSports,
} from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import {
  trackOnboardingStart,
  trackOnboardingSkip,
  trackOnboardingClose,
} from 'src/utils/analytics'

interface OnboardingWizardModalProps {
  isOpen: boolean
  onClose: () => void
  onSkip?: () => void
  onComplete?: () => void
  genericSports: any[]
  sports: any[]
}

const StyledModal = styled(Modal)<{ $isFullScreen?: boolean }>`
  .ant-modal-content {
    background: ${colors.NEUTRAL_800};
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
    @media (max-width: 768px) {
      overflow: hidden;
    }
  }

  .ant-modal-body {
    padding: 0;
  }

  ${(props) =>
    props.$isFullScreen &&
    `
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    max-width: none !important;
    padding: 0 !important;
    margin: 0 !important;

    .ant-modal-content {
      height: 100vh;
      max-height: 100vh;
      margin: 0;
      border-radius: 0;
      display: flex;
      flex-direction: column;
      width: 100vw;
      background: ${colors.SECONDARY_500};
    }

    .ant-modal-body {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 32px 0;
      background: ${colors.SECONDARY_500};
    }
  `}
`

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 16px;
  gap: 24px;
  position: relative;
`

const LogoContainer = styled.div``

const TitleContainer = styled.div`
  text-align: center;
`

const DescriptionContainer = styled.div`
  text-align: center;
`

const StepContent = styled.div`
  width: 60vw;
  max-width: 1080px;
  max-height: 1080px;
  height: 100%;
  background: ${colors.SECONDARY_500};
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    overflow: hidden;
  }
`

const TopHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 16px 16px 16px;
  background: ${colors.SECONDARY_500};
  width: 100%;
  @media (max-width: 768px) {
    padding: 16px 0;
  }
`

const TopLogoContainer = styled.div`
  flex-shrink: 0;
  @media (max-width: 1024px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`

const CloseButtonContainer = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  padding-right: 16px;
  position: absolute;
  right: 0;
  top: 16px;
`

const StepContentBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    min-height: 0;
    overflow-y: auto;
  }
`

const WelcomeCloseButtonContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
`

const WelcomeActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

/**
 * Welcome Step Component - Step 0 of the onboarding wizard
 */
const WelcomeStep: React.FC = () => {
  const { nextStep, skipWizard } = useOnboardingWizard()

  const handleGetStarted = () => {
    console.log('🔄 WelcomeStep: User clicked Get Started')
    trackOnboardingStart()
    nextStep()
  }

  const handleSkipWizard = () => {
    console.log('🔄 WelcomeStep: User skipped wizard from welcome step')
    trackOnboardingSkip('get_started')
    skipWizard()
  }

  const handleCloseWizard = () => {
    console.log('🔄 WelcomeStep: User closed wizard from welcome step')
    trackOnboardingClose('get_started')
    skipWizard()
  }

  return (
    <WelcomeContent>
      <WelcomeCloseButtonContainer>
        <CloseButton onClick={handleCloseWizard} />
      </WelcomeCloseButtonContainer>

      <LogoContainer>
        <SportNinjaLogo />
      </LogoContainer>

      <TitleContainer>
        <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
          Welcome to the Future of Sport!
        </Text>
      </TitleContainer>

      <DescriptionContainer>
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.NEUTRAL_0}
        >
          We are the ultimate hub for Live Scoring, Stats and Participant
          Engagement.
        </Text>
      </DescriptionContainer>

      <WelcomeActionsContainer>
        <Button
          text='Get Started'
          onClick={handleGetStarted}
          variant='primary'
          uppercase
          style={css`
            width: 100%;
          `}
        />

        <SkipLink onClick={handleSkipWizard}>Skip Setup Wizard</SkipLink>
      </WelcomeActionsContainer>
    </WelcomeContent>
  )
}

/**
 * Modal Content Component - Renders the appropriate step based on current state
 */
const ModalContent: React.FC = () => {
  const { state, skipWizard } = useOnboardingWizard()

  const handleCloseFromStep = (stepName: string) => {
    console.log(`🔄 ModalContent: User closed wizard from ${stepName} step`)
    trackOnboardingClose(stepName)
    skipWizard()
  }

  const renderStep = () => {
    switch (state.currentStep) {
      case 0:
        return <WelcomeStep />
      case 1:
        return (
          <StepContent>
            <TopHeader>
              <TopLogoContainer>
                <SportNinjaLogo />
              </TopLogoContainer>
              <CloseButtonContainer>
                <CloseButton
                  onClick={() => handleCloseFromStep('league_layout')}
                />
              </CloseButtonContainer>
            </TopHeader>
            <StepContentBody>
              <LeagueSetup />
            </StepContentBody>
          </StepContent>
        )
      case 2:
        return (
          <StepContent>
            <TopHeader>
              <TopLogoContainer>
                <SportNinjaLogo />
              </TopLogoContainer>
              <CloseButtonContainer>
                <CloseButton
                  onClick={() => handleCloseFromStep('sports_setup')}
                />
              </CloseButtonContainer>
            </TopHeader>
            <StepContentBody>
              <SportsSelection />
            </StepContentBody>
          </StepContent>
        )
      case 3:
        return (
          <StepContent>
            <TopHeader>
              <TopLogoContainer>
                <SportNinjaLogo />
              </TopLogoContainer>
              <CloseButtonContainer>
                <CloseButton
                  onClick={() => handleCloseFromStep('first_competition')}
                />
              </CloseButtonContainer>
            </TopHeader>
            <StepContentBody>
              <FirstCompetition />
            </StepContentBody>
          </StepContent>
        )
      default:
        return <WelcomeStep />
    }
  }

  return renderStep()
}

/**
 * Onboarding Wizard Modal Component
 * Multi-step wizard for new users with SportNinja branding and league setup
 */
const OnboardingWizardModal: React.FC<OnboardingWizardModalProps> = ({
  isOpen,
  onClose,
  onSkip,
  onComplete,
  genericSports,
  sports,
}) => {
  return (
    <OnboardingWizardProvider
      onClose={onClose}
      onSkip={onSkip}
      onComplete={onComplete}
      genericSports={genericSports}
      sports={sports}
    >
      <ModalContentWrapper isOpen={isOpen} />
    </OnboardingWizardProvider>
  )
}

/**
 * Modal Content Wrapper - Handles dynamic modal sizing based on current step
 */
const ModalContentWrapper: React.FC<{
  isOpen: boolean
}> = ({ isOpen }) => {
  const { state } = useOnboardingWizard()
  const isWelcomeStep = state.currentStep === 0
  const isFullScreen = !isWelcomeStep

  return (
    <StyledModal
      open={isOpen}
      onCancel={undefined}
      footer={null}
      closable={false}
      centered={isWelcomeStep}
      width={isWelcomeStep ? 600 : '100vw'}
      $isFullScreen={isFullScreen}
      wrapClassName='onboarding-wizard-modal'
      zIndex={10000}
      styles={{
        body: { padding: 0 },
        mask: {
          backgroundColor: isWelcomeStep
            ? 'rgba(0, 0, 0, 0.8)'
            : colors.SECONDARY_500,
        },
        content: {
          borderRadius: isWelcomeStep ? 16 : 0,
          overflow: 'hidden',
        },
      }}
    >
      <ModalContent />
    </StyledModal>
  )
}

const mapStateToProps = (state: any) => {
  return {
    genericSports: getAllGenericSportProperties(state),
    sports: Object.values(getSports(state)),
  }
}

export default connect(mapStateToProps, null)(OnboardingWizardModal)
